/**
 * Authentication service for interactive authentication with 360°
 * Similar to teams-react-app's authenticateTo360 but using Office.js displayDialogAsync
 */

import { ApiConfig, getEnvironment360BizURL } from '../configs/appConfig';

interface AuthResult {
    success: boolean;
    sessionToken?: string;
    expiresIn?: number;
    userName?: string;
    error?: string;
}

type DialogEvent =
    | { message: string; origin: string | undefined }
    | { error: number };

/**
 * Authenticates to 360° using Office dialog-based OAuth flow
 * @param p360url - The base URL of the 360° instance
 * @param forceRefresh - Whether to force a new authentication even if session exists
 * @returns Promise that resolves with the authentication result
 */
export async function authenticateTo360(p360url: string, forceRefresh?: boolean): Promise<string> {
    // Normalize URL
    p360url = p360url.endsWith('/') ? p360url.slice(0, -1) : p360url;

    const sessionKey = `${p360url}_p360Session`;
    const sessionExpiryKey = `${p360url}_p360SessionExpiry`;
    const sessionExpiry = sessionStorage.getItem(sessionExpiryKey);
    const currentTime = Date.now();

    // Force refresh removes cached session
    if (forceRefresh) {
        sessionStorage.removeItem(sessionKey);
        sessionStorage.removeItem(sessionExpiryKey);
    }
    // Check if session exists and not expired
    else if (sessionExpiry && currentTime < Number.parseInt(sessionExpiry)) {
        // Verify with server that session is still valid
        const isValid = await verifyAuthentication(p360url);
        if (isValid) {
            return sessionStorage.getItem(sessionKey) as string;
        }
        // Session invalid on server, clear it and continue to re-authenticate
        sessionStorage.removeItem(sessionKey);
        sessionStorage.removeItem(sessionExpiryKey);
    }

    // Construct the authentication URL
    const returnUrl = `${globalThis.location.origin}/authorize.html`;
    // Use our own domain's redirect page to avoid AppDomains restriction
    const dialogUrl = `${globalThis.location.origin}/auth-redirect.html?p360url=${encodeURIComponent(p360url)}&returnUrl=${encodeURIComponent(returnUrl)}`;

    return new Promise<string>((resolve, reject) => {
        // Open authentication dialog using Office.js
        Office.context.ui.displayDialogAsync(
            dialogUrl,
            {
                height: 60,
                width: 40,
                promptBeforeOpen: false,
                displayInIframe: false // Important for authentication flows
            },
            (asyncResult) => {
                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                    reject(new Error(`Failed to open authentication dialog: ${asyncResult.error.message}`));
                    return;
                }

                const dialog = asyncResult.value;

                // Handle messages from the dialog
                dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: DialogEvent) => {
                    dialog.close();

                    if ('message' in arg) {
                        try {
                            const messageFromDialog = JSON.parse(arg.message) as AuthResult;

                            if (messageFromDialog.success && messageFromDialog.sessionToken) {
                                // Store the session token

                                sessionStorage.setItem(sessionKey, messageFromDialog.sessionToken);
                                // Use expiresIn from response (in seconds), convert to milliseconds
                                const expiryTime = currentTime + (messageFromDialog.expiresIn || 3600) * 1000;
                                sessionStorage.setItem(sessionExpiryKey, expiryTime.toString());
                                resolve(messageFromDialog.sessionToken);
                            } else {
                                reject(new Error(messageFromDialog.error || "Authentication failed"));
                            }
                        } catch (error) {
                            const errorMessage = error instanceof Error ? error.message : "Failed to parse authentication response";
                            reject(new Error(errorMessage));
                        }
                    }
                });

                // Handle dialog events (close, error)
                dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg: DialogEvent) => {
                    dialog.close();

                    if ('error' in arg) {
                        if (arg.error === 12006) {
                            // User closed the dialog
                            reject(new Error("Authentication cancelled by user"));
                        } else {
                            reject(new Error(`Authentication dialog error: ${arg.error}`));
                        }
                    }
                });
            }
        );
    });
}

/**
 * Verifies authentication by calling Ping endpoint
 * @param p360url - The base URL of the 360° instance
 * @returns Promise that resolves to true if authenticated, false otherwise
 */
async function verifyAuthentication(p360url: string): Promise<boolean> {
    try {
        const sessionKey = `${p360url}_p360Session`;
        const token = sessionStorage.getItem(sessionKey);

        // If no session token exists, no point in verifying
        if (!token) {
            return false;
        }

        // Verify with server using cookies (interactive auth uses cookie-based auth)
        const response = await fetch(getEnvironment360BizURL() + '/Biz/v2/api/call' + ApiConfig.checkConnection, {
            method: 'POST',
            credentials: 'include', // Send cookies for authentication
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify({})
        });

        return response.ok;
    } catch {
        // Authentication verification failed
        return false;
    }
}

/**
 * Gets the current authentication mode from configuration
 * @returns true if interactive authentication is enabled
 */
export function isInteractiveAuthEnabled(): boolean {
    const authMode = localStorage.getItem("authMode");
    return authMode === "interactive";
}

/**
 * Sets the authentication mode
 * @param useInteractive - Whether to use interactive authentication
 */
export function setAuthMode(useInteractive: boolean): void {
    localStorage.setItem("authMode", useInteractive ? "interactive" : "token");
}

/**
 * Gets whether multiple mail archive is enabled
 * @returns true when enabled, false when disabled
 */
export function isMultipleMailArchiveEnabled(): boolean {
    return localStorage.getItem("multipleMailArchiveMode") !== "disabled";
}

/**
 * Sets the multiple mail archive mode
 * @param isEnabled - Whether multiple mail archive should be enabled
 * Only sets the value if not already configured, allowing DevTools override for testing
 */
export function setMultipleMailArchiveEnabled(isEnabled: boolean): void {
    localStorage.setItem('multipleMailArchiveMode', isEnabled ? 'enabled' : 'disabled');
}
